import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Select,
  Icon,
  Grid,
  GridItem,
  useColorModeValue,
  useToast,
  Button
} from '@chakra-ui/react';
import {
  FaMoneyBillWave,
  FaCalendarAlt,
  FaChartLine,
  FaExchangeAlt,
  FaTicketAlt,
  FaCoins,
  FaDollarSign,
  FaPercent,
  FaCalculator
} from 'react-icons/fa';
import axios from 'axios';
import { AuthContext } from '../../../contexts/AuthContext';
import { DatePicker } from 'antd';
import moment from 'moment';

export default function Faturamento() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [faturamentoData, setFaturamentoData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { authInfo } = useContext(AuthContext);
  const toast = useToast();

  useEffect(() => {
    const fetchFaturamentoData = async () => {
      setIsLoading(true);
      try {
        let queryString = '';

        if (startDate && endDate) {
          const formattedStartDate = moment(startDate).format('YYYY-MM-DD HH:mm:ss');
          const formattedEndDate = moment(endDate).format('YYYY-MM-DD HH:mm:ss');
          queryString = `startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_SERVIDOR}/faturamento-stats${queryString ? `?${queryString}` : ''}`,
          {
            headers: {
              "x-access-token": authInfo?.dataUser?.token,
              "content-type": "application/json",
            },
          }
        );

        setFaturamentoData(response.data);
      } catch (error) {
        console.error("Erro ao buscar dados de faturamento:", error);
        toast({
          title: "Erro ao carregar dados",
          description: "Não foi possível carregar os dados de faturamento.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchFaturamentoData();
  }, [startDate, endDate, authInfo?.dataUser?.token, toast]);

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const bgColor = useColorModeValue('#1F2630', '#1F2630');
  const textColor = useColorModeValue('white', 'white');
  const borderColor = useColorModeValue('#2D3748', '#2D3748');

  if (isLoading || !faturamentoData) {
    return (
      <Flex justify="center" align="center" h="500px">
        <Text fontSize="xl" color={textColor}>Carregando dados de faturamento...</Text>
      </Flex>
    );
  }

  // Safely get data with defaults
  const currentMonth = startDate ? new Date(startDate).getMonth() : new Date().getMonth();
  const currentMonthData = faturamentoData?.faturamentoMensal?.find(m => m.mes === currentMonth + 1) || { total: 0 };
  const previousMonthIndex = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousMonthData = faturamentoData?.faturamentoMensal?.find(m => m.mes === previousMonthIndex + 1) || { total: 0 };
  
  const percentChange = previousMonthData.total > 0
    ? ((currentMonthData.total - previousMonthData.total) / previousMonthData.total) * 100
    : 0;

  const ticketMedio = faturamentoData?.ticketMedio?.[0]?.media || 0;
  const totalTransacoes = faturamentoData?.totalTransacoes || 0;
  const totalPagamentosNoBanco = faturamentoData?.totalPagamentosNoBanco || 0;

  return (
    <Box p={[2, 3, 5]} h={["auto", "auto", "100vh"]}>
      <Flex
        direction="column"
        bg={bgColor}
        p={[3, 4, 6]}
        borderRadius="xl"
        boxShadow="xl"
        color={textColor}
        h="100%"
      >
        <Flex 
          justify="space-between" 
          align={["flex-start", "flex-start", "center"]} 
          mb={6}
          direction={["column", "column", "row"]}
          gap={4}
        >
          <Heading size={["md", "lg"]} display="flex" alignItems="center" color={"white"} mb={[4, 4, 0]}>
            <Icon as={FaMoneyBillWave} mr={2} />
            Faturamento da Plataforma
          </Heading>

          <Flex 
            gap={4} 
            alignItems="flex-start"
            direction={["column", "column", "row"]}
            w={["100%", "100%", "auto"]}
          >
            <Flex direction="column" w={["100%", "100%", "auto"]}>
              <Text color="white" mb={2}>Data Inicial</Text>
              <DatePicker
                value={startDate ? moment(startDate) : null}
                style={{
                  border: "1px solid #1F2630",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  width: "100%"
                }}
                showTime={{ format: "HH:mm" }}
                format="DD-MM-YYYY HH:mm"
                placeholder="Selecione a data e hora"
                onChange={(date) => {
                  setStartDate(date ? date.format('YYYY-MM-DD HH:mm:ss') : null);
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Flex>

            <Flex direction="column" w={["100%", "100%", "auto"]}>
              <Text color="white" mb={2}>Data Final</Text>
              <DatePicker
                value={endDate ? moment(endDate) : null}
                style={{
                  border: "1px solid #1F2630",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  width: "100%"
                }}
                showTime={{ format: "HH:mm" }}
                format="DD-MM-YYYY HH:mm"
                placeholder="Selecione a data e hora"
                onChange={(date) => {
                  setEndDate(date ? date.format('YYYY-MM-DD HH:mm:ss') : null);
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Flex>

            <Button
              colorScheme="blue"
              size="md"
              onClick={handleClearFilters}
              mt={["2", "2", "32px"]}
              w={["100%", "100%", "auto"]}
            >
              Limpar Filtros
            </Button>
          </Flex>
        </Flex>

        <Grid 
          templateColumns={[
            "repeat(1, 1fr)", 
            "repeat(2, 1fr)", 
            "repeat(2, 1fr)",
            "repeat(4, 1fr)"
          ]} 
          gap={[3, 4, 6]} 
          mb={8}
        >
          <GridItem>
            <Stat
              p={4}
              borderRadius="lg"
              bg="whiteAlpha.100"
              boxShadow="md"
            >
              <StatLabel fontSize="md" display="flex" alignItems="center">
                <FaDollarSign style={{ marginRight: "8px", color: "white" }} />
                Faturamento
              </StatLabel>
              <StatNumber fontSize="2xl">{formatCurrency(currentMonthData.total)}</StatNumber>
              <StatHelpText display="flex" alignItems="center">
                <StatArrow type={percentChange >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(percentChange).toFixed(2)}% em relação ao mês anterior
              </StatHelpText>
            </Stat>
          </GridItem>

          <GridItem>
            <Stat
              p={4}
              borderRadius="lg"
              bg="whiteAlpha.100"
              boxShadow="md"
            >
              <StatLabel fontSize="md" display="flex" alignItems="center">
                <FaExchangeAlt style={{ marginRight: "8px", color: "white" }} />
                Transações
              </StatLabel>
              <StatNumber fontSize="2xl">{totalTransacoes}</StatNumber>
              <StatHelpText>
                Total de operações realizadas
              </StatHelpText>
            </Stat>
          </GridItem>

          <GridItem>
            <Stat
              p={4}
              borderRadius="lg"
              bg="whiteAlpha.100"
              boxShadow="md"
            >
              <StatLabel fontSize="md" display="flex" alignItems="center">
                <FaTicketAlt style={{ marginRight: "8px", color: "white" }} />
                Ticket Médio
              </StatLabel>
              <StatNumber fontSize="2xl">
                {formatCurrency(ticketMedio)}
              </StatNumber>
              <StatHelpText>
                Valor médio por transação
              </StatHelpText>
            </Stat>
          </GridItem>

          <GridItem>
            <Stat
              p={4}
              borderRadius="lg"
              bg="whiteAlpha.100"
              boxShadow="md"
            >
              <StatLabel fontSize="md" display="flex" alignItems="center">
                <FaCoins style={{ marginRight: "8px", color: "white" }} />
                Pagamentos no Banco
              </StatLabel>
              <StatNumber fontSize="2xl">{totalPagamentosNoBanco}</StatNumber>
              <StatHelpText>
                Total de pagamentos registrados
              </StatHelpText>
            </Stat>
          </GridItem>
        </Grid>
      </Flex>
    </Box>
  );
}
